import { createFileRoute, redirect } from '@tanstack/react-router';
import { doNothing } from 'remeda';
import { z } from 'zod';

import config from '@@config';
import { restoreSavedFilters } from '@@containers/Search/restoreSavedFilters';
import { requireRoles } from '@@router/guards';
import { getContentLocale } from '@@scripts/store/settings/useSettingsStore';

import { teasersQuery } from './-components/Content/teasersQuery';
import { teasersSearchParams } from './-components/Content/teasersSearchParams';
import { getPersistedColumns } from './-components/hooks/usePersistedColumns';
import { Layout } from './-components/Layout';

const requirePlacementRoles = requireRoles(config.navigation.placement.authorize);

export const Route = createFileRoute('/_authenticated/placement')({
    validateSearch: teasersSearchParams.extend({
        // The ids of the pages shown as columns, in their order
        columns: z.array(z.string()).optional().catch(undefined),
    }),
    // A new search of the teasers makes a new match, which runs the loader again
    loaderDeps: ({ search }) => teasersSearchParams.parse(search),
    beforeLoad: ({ cause, location, search }) => {
        requirePlacementRoles();

        // On entering without columns, or with columns the schema dropped such as the single column
        // of an old link, the ones the user left open in this language
        const columns =
            cause === 'enter' && search.columns === undefined
                ? getPersistedColumns(getContentLocale())
                : [];
        const restored = columns.length ? { ...search, columns } : search;

        // One redirect restores both the saved filters and the columns
        restoreSavedFilters({ cause, location, search: restored }, teasersSearchParams, 'default');

        if (restored !== search) {
            throw redirect({ to: '.', search: restored, replace: true });
        }

        // Built on every load, so the options follow the content language as well as the search
        return { teasersQuery: teasersQuery(search, getContentLocale()) };
    },
    // The sidebar opens on the teasers
    loader: ({ context }) => {
        context.queryClient.infiniteQuery(context.teasersQuery).catch(doNothing());
    },
    component: Layout,
});
